import type { SxProps, Theme } from '@mui/material/styles';

const compactFloatingLabel = {
  fontSize: '0.75rem',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '18px',
  letterSpacing: '0.25px',
  paddingTop: '2px',
  paddingLeft: '1px',
} as const;

// --- Shrink / floating labels (filled TextField, Select, DatePicker, MuiTelInput, Autocomplete) ---
// Plain objects so they spread cleanly into MUI `sx` without SxProps array/function widening.

/** Compact filled fields — single source for label size (matches entity form text inputs). */
export const compactFilledFloatingLabelSx = { ...compactFloatingLabel };

/** Non-compact filled controls (toolbars, full-size selects). */
export const defaultFilledFloatingLabelSx = {
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '20px',
  letterSpacing: '0.25px',
  paddingTop: '3px',
  paddingLeft: '1px',
};

// --- Value text inside filled input / select ---

export const compactFilledInputValueSx = {
  fontSize: '0.8125rem',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '18px',
  letterSpacing: '0.25px',
};

/** Dropdown list items for compact filled selects (merge into `MenuProps.PaperProps.sx`). */
export const compactFilledSelectMenuPaperSx: SxProps<Theme> = {
  '& .MuiMenuItem-root': {
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    minHeight: 34,
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '12px',
    paddingRight: '12px',
  },
};

/**
 * Bordered filled `size="small"` TextField vs Select: MUI Select is ~0.5–1px shorter by default.
 * Lock both to the same outer box (includes 1px border) so rows align in entity forms.
 */
const COMPACT_FILLED_BORDERED_CONTROL_MIN_HEIGHT_PX = 45.29;

/** Same outer box as TextField (min + height) — Select needs both or it stays slightly short. */
export const compactFilledBorderedInputFixedBoxSx = {
  minHeight: `${COMPACT_FILLED_BORDERED_CONTROL_MIN_HEIGHT_PX}px`,
  height: `${COMPACT_FILLED_BORDERED_CONTROL_MIN_HEIGHT_PX}px`,
  boxSizing: 'border-box' as const,
  // MUI `size="small"` applies min-height ~1.4375em on the inner node; unset so the px box wins.
  '& .MuiInputBase-input': { minHeight: 0 },
  '& .MuiSelect-select': { minHeight: 0 },
};

/**
 * Closed Select display — mirrors MUI filled `size="small"` `.MuiInputBase-input` padding on TextField
 * (do not override TextField inner padding). Extra `paddingRight` for the dropdown icon.
 */
export const compactFilledBorderedSelectDisplayPadding = {
  paddingTop: '21px',
  paddingBottom: '4px',
  paddingLeft: '12px',
  paddingRight: '32px',
  boxSizing: 'border-box' as const,
};

/**
 * FormRenderer filled Select: same typography as `compactFilledFloatingLabelSx`, with padding
 * and shrink transform aligned to MUI filled `size="small"` (must keep `scale(0.75)` — omitting
 * it made the label render larger than compact TextField labels).
 */
export const compactFilledSelectInputLabelSx = {
  ...compactFilledFloatingLabelSx,
  paddingTop: 0,
  '&.MuiInputLabel-shrink': {
    transform: 'translate(12px, 4px) scale(0.75)',
    transformOrigin: 'top left',
  },
};

/** FormControl spacing only (avoid duplicating label rules on parent + label node). */
export const compactFilledFormControlSpacingSx = {
  marginTop: 0,
  marginBottom: 0,
};

export const defaultFilledInputValueSx = {
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '20px',
  letterSpacing: '0.25px',
};

// --- TextField-based fields (NumberField, TextInputField, Email % field, etc.) ---

/** InputLabel only under FormControl — use for Select so value font comes from Select `sx`, not TextField-sized `MuiInputBase-root`. */
export const compactFilledFloatingLabelCascadeSx: SxProps<Theme> = {
  '& .MuiInputLabel-root': { ...compactFloatingLabel },
};

/** Typography for filled entity/form-builder fields (applied on field components, not FormRenderer). */
export const compactFilledTextFieldSx: SxProps<Theme> = {
  ...compactFilledFloatingLabelCascadeSx,
  '& .MuiInputBase-root': {
    fontSize: '0.8125rem',
  },
  '& .MuiInputBase-input': {
    fontSize: '0.8125rem',
  },
};

/** Error/helper line under compact filled fields */
export const compactFilledFieldHelperTextSx = {
  fontSize: '0.75rem',
};

/** FormControlLabel + checkbox/radio row (entity forms). */
export const compactFormControlLabelSx: SxProps<Theme> = {
  marginY: 0,
  // Top-align so multi-line labels keep the control beside the first line.
  alignItems: 'flex-start',
  '& .MuiFormControlLabel-label': {
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    // SwitchBase (Checkbox/Radio) has 9px internal padding around the icon.
    paddingY: '9px',
  },
};

/** FormLabel above checkbox/radio groups (matches compact filled floating label size). */
export const compactFormGroupLabelSx: SxProps<Theme> = {
  fontSize: '0.75rem',
  mb: 0.5,
};
